import { formatTime } from "@/features/flight-estimator/_logic/utils";

import type { RouteFlight } from "./types";

/** A flight as one or two short lines of text. */
export interface FlightLines {
  /** "TAP Air Portugal TP 1351" */
  title: string | null;
  /** "Direct · 2h 45m · Economy", "1 stop via MAD · 6h 10m" */
  route: string;
  /** "18:55 → 21:45" when the search returned times. */
  times: string | null;
}

const clock = (time: string | null): string | null => time?.match(/\d{1,2}:\d{2}$/)?.[0] ?? null;

/**
 * Words for a fare's flight: airline and number, stops (with connection airports), duration,
 * cabin, and outbound times.
 * @param flight - The flight behind a fare.
 * @returns Display lines.
 * @throws Never.
 */
export const describeFlight = (flight: RouteFlight): FlightLines => {
  const title = [flight.airline, flight.flightNumber].filter(Boolean).join(" ") || null;
  const stops =
    flight.stops === 0
      ? "Direct"
      : `${flight.stops} ${flight.stops === 1 ? "stop" : "stops"}${flight.via.length ? ` via ${flight.via.join(", ")}` : ""}`;
  const route = [stops, flight.minutes ? formatTime(flight.minutes) : null, flight.cabin].filter(Boolean).join(" · ");
  const departs = clock(flight.departs);
  const arrives = clock(flight.arrives);
  return { title, route, times: departs && arrives ? `${departs} → ${arrives}` : null };
};

/** A fare's flight as separate parts, so a narrow column can show the most useful first. */
export interface FlightParts {
  airline: string | null;
  /** "UA 2491". */
  number: string | null;
  /** "Direct", "1 stop". */
  stops: string;
  /** "1 stop via DEN". */
  stopsVia: string;
  /** Connecting airports ("KEF, ORD"), shown whole or not at all; null for a direct flight. */
  via: string | null;
  /** "3h 22m". */
  duration: string | null;
  /** "09:36 → 10:58". */
  times: string | null;
  /** The airline's cabin name, only when it differs from the trip's cabin. */
  cabin: string | null;
  /** Everything, for a tooltip. */
  full: string;
}

/**
 * Splits a fare's flight into parts in the order a narrow column drops them last to first:
 * stops, flight number, duration, times. The cabin is left out when it matches the trip's.
 * @param flight - The flight behind a fare.
 * @param tripCabin - The cabin the group flies, e.g. "economy".
 * @returns The parts.
 * @throws Never.
 */
export const flightParts = (flight: RouteFlight, tripCabin: string): FlightParts => {
  const stops = flight.stops === 0 ? "Direct" : `${flight.stops} ${flight.stops === 1 ? "stop" : "stops"}`;
  const stopsVia = flight.via.length ? `${stops} via ${flight.via.join(", ")}` : stops;
  const duration = flight.minutes ? formatTime(flight.minutes) : null;
  const departs = clock(flight.departs);
  const arrives = clock(flight.arrives);
  const times = departs && arrives ? `${departs} → ${arrives}` : null;
  const cabin = flight.cabin && flight.cabin.toLowerCase() !== tripCabin.toLowerCase() ? flight.cabin : null;
  const full = [
    [flight.airline, flight.flightNumber].filter(Boolean).join(" "),
    stopsVia,
    duration,
    flight.cabin,
    times,
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    airline: flight.airline,
    number: flight.flightNumber,
    stops,
    stopsVia,
    via: flight.via.length ? flight.via.join(", ") : null,
    duration,
    times,
    cabin,
    full,
  };
};
